import { BodyText, Button, Tooltip, toast } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import {
  History,
  ListTree,
  type LucideIcon,
  Stethoscope,
  ThumbsDown,
} from "lucide-react";
import { AssessmentUnavailableReason } from "../network/useAssessment";
import type { AssessmentGenerationStatus } from "../network/useAssessmentGenerationStatus";
import {
  AssessmentFeedbackType,
  useAssessmentFeedback,
} from "./useAssessmentFeedback";

const ASSESSMENT_GENERATION_FAILED_MESSAGE =
  "AI Assessment generation failed. Try generating again, and fallback to manual entry if issues persist.";
const ASSESSMENT_GENERATION_UNAVAILABLE_REASON_MESSAGES: Partial<
  Record<AssessmentUnavailableReason, string>
> = {
  [AssessmentUnavailableReason.VISIT_IS_CHAI_ASYNC]:
    "AI Assessment Generation is not available for Async CHAI visits. Manually write the visit assessment.",
  [AssessmentUnavailableReason.PEDIATRIC_PATIENT]:
    "AI Assessment Generation is not available for pediatric patients. Manually write the visit assessment.",
};

const ASSESSMENT_FEEDBACK_OPTIONS: {
  feedbackType: AssessmentFeedbackType;
  Icon: LucideIcon;
  label: string;
}[] = [
  {
    feedbackType: AssessmentFeedbackType.CLINICAL_FIDELITY_FEEDBACK,
    Icon: Stethoscope,
    label: "Clinical Accuracy",
  },
  {
    feedbackType: AssessmentFeedbackType.PMH_ACCURACY_FEEDBACK,
    Icon: History,
    label: "Referenced PMH Accuracy",
  },
  {
    feedbackType: AssessmentFeedbackType.ORGANIZATION_FEEDBACK,
    Icon: ListTree,
    label: "Formatting",
  },
];

export const AssessmentGenerationControls = ({
  isAssessmentSaving,
  encounterId,
  feedbackBtSpanId,
  hasPreviouslyGeneratedAssessment,
  isAssessmentFlagged,
  isInProgress,
  patientId,
  readOnly,
  shouldShowHigherReasoningNotice,
  showControls,
  start,
  status,
  unavailableReason,
}: {
  isAssessmentSaving: boolean;
  encounterId?: string;
  feedbackBtSpanId?: string;
  hasPreviouslyGeneratedAssessment: boolean;
  isAssessmentFlagged: boolean;
  isInProgress: boolean;
  patientId?: string;
  readOnly: boolean;
  shouldShowHigherReasoningNotice?: boolean;
  showControls: boolean;
  start: () => Promise<void>;
  status?: AssessmentGenerationStatus;
  unavailableReason?: AssessmentUnavailableReason;
}) => {
  const assessmentFeedback = useAssessmentFeedback({ patientId, encounterId });

  if (!showControls || isAssessmentSaving) {
    return null;
  }

  const unavailableReasonMessage = unavailableReason
    ? ASSESSMENT_GENERATION_UNAVAILABLE_REASON_MESSAGES[unavailableReason]
    : undefined;

  if (unavailableReasonMessage) {
    return (
      <BodyText size="xs" textColor="tertiary">
        {unavailableReasonMessage}
      </BodyText>
    );
  }

  const handleGenerateAssessment = async () => {
    if (readOnly || isInProgress || unavailableReason != null) {
      return;
    }

    trackEvent(
      hasPreviouslyGeneratedAssessment
        ? TRACKERS.REGENERATE_ASSESSMENT_V2
        : TRACKERS.GENERATE_ASSESSMENT_V2,
    );

    try {
      await start();
    } catch {
      toast.error("Failed to generate assessment");
    }
  };

  const handleAssessmentFeedback = async (
    feedbackType: AssessmentFeedbackType,
  ) => {
    if (
      readOnly ||
      !feedbackBtSpanId ||
      isAssessmentFlagged ||
      isInProgress ||
      assessmentFeedback.isPending
    ) {
      return;
    }

    trackEvent(TRACKERS.FLAG_ASSESSMENT_FOR_REVIEW);

    try {
      await assessmentFeedback.mutateAsync({
        btSpanId: feedbackBtSpanId,
        feedbackType,
      });
    } catch {
      toast.error("Failed to submit assessment feedback");
    }
  };

  const buttonLabel = isInProgress
    ? "Generating Assessment"
    : hasPreviouslyGeneratedAssessment
      ? "Regenerate Assessment"
      : "Generate Assessment";
  const feedbackButtonLabel = isAssessmentFlagged
    ? "Assessment Issue Reported"
    : "Report Incorrect Assessment";
  const isFeedbackDisabled =
    readOnly || isAssessmentFlagged || assessmentFeedback.isPending;

  const buttons = (
    <div className="flex w-full flex-wrap items-center gap-2">
      <Button
        size="xs"
        onClick={handleGenerateAssessment}
        className="w-fit bg-white border-success text-success"
        variant="secondary"
        disabled={readOnly || unavailableReason != null}
        isLoading={isInProgress}
        hideTextOnLoading={false}
        aria-busy={isInProgress || undefined}
      >
        {buttonLabel}
      </Button>
      {feedbackBtSpanId && !isInProgress && (
        <DropdownMenu>
          <Tooltip
            side="top"
            tooltipContentText={feedbackButtonLabel}
            triggerClassName="ml-auto leading-none"
          >
            <DropdownMenuTrigger asChild>
              <Button
                size="xs"
                className="h-6 w-6 !p-1 text-link"
                variant="icon"
                disabled={isFeedbackDisabled}
                isLoading={assessmentFeedback.isPending}
                aria-busy={assessmentFeedback.isPending || undefined}
                aria-label={feedbackButtonLabel}
              >
                <ThumbsDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end">
            {ASSESSMENT_FEEDBACK_OPTIONS.map(
              ({ feedbackType, Icon, label }) => (
                <DropdownMenuItem
                  key={feedbackType}
                  className="gap-2"
                  disabled={isFeedbackDisabled}
                  onSelect={() => {
                    void handleAssessmentFeedback(feedbackType);
                  }}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </DropdownMenuItem>
              ),
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  if (status === "FAILED" && !isInProgress && unavailableReason == null) {
    return (
      <div className="flex flex-col items-start gap-1">
        {buttons}
        <BodyText size="xs" textColor="error">
          {ASSESSMENT_GENERATION_FAILED_MESSAGE}
        </BodyText>
      </div>
    );
  }

  if (shouldShowHigherReasoningNotice) {
    return (
      <div className="flex flex-col items-start gap-1">
        {buttons}
        <BodyText size="xs" textColor="tertiary">
          Assessment Generation may take longer due to extensive medical
          history.
        </BodyText>
      </div>
    );
  }

  return buttons;
};
